import { AxiosError } from 'axios'
import { action, computed, flow, makeObservable, observable } from 'mobx'

import { requestWrapper } from 'Shared/utils/network'

import { STORE_UI } from 'Core/constants'
import { CustomGenerator, SortingParams } from 'Core/interfaces'
import { TableStore } from 'Core/stores/tableStore/TableStore'
import {
	entityToLabelWithDashValue,
	entityToStringLabelValue,
	getDefaultStoreArrayValue,
	getDefaultStoreValue,
	prepareGenericParams,
	setURLParams
} from 'Core/utils'
import { storagePageSizeConstant } from 'Core/utils/storeHelpers/storagePageSizeConstant'

import { getAllOptions, STORE_METRIC_BY_PERIODS, UrlParams } from 'Portals/Customer/constants'
import { Kpi, Location, LocationByPeriodFilterFields } from 'Portals/Customer/entities'
import { getFiltersConfig } from 'Portals/Customer/Pages/LocationByPeriods/TableList/FiltersWrapper/Filters/utils/config'
import { KpiRepo, LocationRepo } from 'Portals/Customer/repositories'
import { RootStore } from 'Portals/Customer/stores'

const STORAGE_LOCATIONS_BY_PERIOD_PAGE_SIZE = storagePageSizeConstant('locationsByPeriod')

export class LocationByPeriodsTable extends TableStore<Kpi.KpiLocationMeasure> {
	@observable locations = getDefaultStoreArrayValue<Location.LocationFilter>()
	@observable kpiCategoriesWithKpis = getDefaultStoreArrayValue<Kpi.OptionsByserviceSiteId>()
	@observable kpiById = getDefaultStoreValue<Kpi.Basic>()
	@observable mobileSorting: SortingParams | null = null

	constructor(private rootStore: RootStore) {
		super(STORAGE_LOCATIONS_BY_PERIOD_PAGE_SIZE)
		makeObservable(this)
	}

	@flow.bound
	*initFromURLParams(params: Record<string, string[]>) {
		yield this.retrieveKpiOptionsByLocation(params[UrlParams.serviceSiteId]?.[0])
		yield this.retrieveKpiById(params[UrlParams.kpiId]?.[0])

		const filterKeys = [
			UrlParams.serviceSiteId,
			UrlParams.kpiCategoryId,
			UrlParams.kpiId,
			UrlParams.year
		]
		this.initFilterParamsFromUrl(params, filterKeys, getFiltersConfig())

		this.getItems()
	}

	@flow.bound
	*getItems(): CustomGenerator<Kpi.KpiLocationMeasure[]> {
		const selectedLocation = this.filters[LocationByPeriodFilterFields.serviceSiteId]?.data
		const selectedKpi = this.filters[LocationByPeriodFilterFields.kpiId]?.data

		if (!selectedLocation || !selectedKpi) {
			return
		}

		setURLParams({ searchFilters: this.searchFilters, filters: this.filters })

		try {
			this.isLoading = true
			const genericParams = prepareGenericParams(this)
			const { page, pageSize, sortField, sortOrder, kpiCategoryId, ...params } = genericParams
			const data = yield KpiRepo.retrieveKpiMeasureData({
				...params,
				includeChange: true
			} as Kpi.MeasureRequest)
			this.items = data
			this.setMobileSorting(this.sortingParams)
		} catch (error) {
			this.rootStore[STORE_UI].showErrorNotification(error as AxiosError)
		} finally {
			this.isLoading = false
		}
	}

	@action.bound
	clearTable() {
		this.resetTableItems()
		this.clearFilters()
		this.clearSorting()
		this.setMobileSorting(null)
		this.kpiCategoriesWithKpis = getDefaultStoreArrayValue()
	}

	@flow.bound
	*retrieveKpiById(kpiId: Key) {
		yield requestWrapper({
			field: this.kpiById,
			repository: KpiRepo.getKpiById,
			repositoryParams: kpiId
		})
	}

	@computed
	get kpiYearsOptions() {
		return [getAllOptions(), ...this.rootStore[STORE_METRIC_BY_PERIODS].kpiMetricYearOptions]
	}

	@flow.bound
	*retrieveLocationOptions() {
		yield requestWrapper({
			field: this.locations,
			repository: LocationRepo.retrieveLocationOptions,
			repositoryParams: null
		})
	}

	@computed
	get locationOptions() {
		return this.locations.value.map(entityToLabelWithDashValue)
	}

	@flow.bound
	*retrieveKpiOptionsByLocation(serviceSiteId: Key) {
		yield requestWrapper({
			field: this.kpiCategoriesWithKpis,
			repository: KpiRepo.retrieveKpiOptionsByLocation,
			repositoryParams: serviceSiteId
		})
	}

	@computed
	get kpiCategoryOptions() {
		return this.kpiCategoriesWithKpis.value.map(({ category }) =>
			entityToStringLabelValue(category as Entity)
		)
	}

	@computed
	get kpiOptions() {
		const selectedKpiCategory = this.filters[LocationByPeriodFilterFields.kpiCategoryId]?.data

		if (!selectedKpiCategory) {
			return [] as LabelValue[]
		}

		const kpisByCategory = this.kpiCategoriesWithKpis.value.find(
			({ category }) => category.id === Number(selectedKpiCategory)
		)?.kpis

		return kpisByCategory!.map(entityToStringLabelValue)
	}

	@flow.bound
	*changeSelectFilter(filterName: LocationByPeriodFilterFields, value: string) {
		this.resetTableItems()

		try {
			switch (filterName) {
				case LocationByPeriodFilterFields.serviceSiteId: {
					this.resetKpiCategoryFilter()
					this.resetKpiFilter()
					this.resetKpiYearFilter()
					yield this.retrieveKpiOptionsByLocation(value)
					break
				}

				case LocationByPeriodFilterFields.kpiCategoryId: {
					this.resetKpiFilter()
					this.resetKpiYearFilter()
					break
				}

				case LocationByPeriodFilterFields.kpiId: {
					yield this.retrieveKpiById(value)
					this.resetKpiYearFilter()
					break
				}
			}
		} catch (error) {
			this.rootStore[STORE_UI].showErrorNotification(error as AxiosError)
		}
	}

	@action
	resetKpiCategoryFilter() {
		this.kpiCategoriesWithKpis = getDefaultStoreArrayValue()
		delete this.filters[LocationByPeriodFilterFields.kpiCategoryId]
	}

	@action
	resetKpiFilter() {
		delete this.filters[LocationByPeriodFilterFields.kpiId]
	}

	@action
	resetKpiYearFilter() {
		delete this.filters[LocationByPeriodFilterFields.year]
	}

	@action
	resetTableItems() {
		this.kpiById = getDefaultStoreValue()
		this.items = []
	}

	@computed
	get getLocation() {
		const serviceSiteId = Number(this.filters[LocationByPeriodFilterFields.serviceSiteId]?.data)
		const name = this.locations.value.find(({ id }) => id === serviceSiteId)?.name

		return {
			id: serviceSiteId,
			name
		}
	}

	@action
	setMobileSorting = (value: SortingParams | null) => {
		this.mobileSorting = value
	}

	@computed
	get selectedKpi() {
		return this.kpiById.value
	}
}
